import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Save, Users, Loader2, Trash2, Target, AlertTriangle, Plus, ExternalLink, Package, Filter, Sparkles, Send, Check, Paperclip, MessageSquare, Layers } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { personasApi, featuresApi, epicsApi, copilotApi, unifiedCopilotApi, teamApi } from '../../services/api';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useAuth } from '../../contexts/AuthContext';
import { ModalZIndexProvider } from '../../contexts/ModalZIndexContext';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import FeatureDetailModal from './FeatureDetail';
import { UnifiedCoPilot } from '../copilot';
import { NotesPanel, AttachmentsPanel } from '../shared';

// Shared modules (centralized)
import { useThinkingPhrases, handleAIChatError } from '../copilot/shared';
import CopilotMarkdown from '../copilot/CopilotMarkdown';
import { parseAIResponse } from '../copilot/utils/actionParser';

// Status badge colors
const STATUS_COLORS = {
  'Backlog': 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  'Ready': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  'In Progress': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  'Done': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  'Blocked': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

// Add Feature Modal - supports linking existing or creating new
const AddFeatureModal = ({ isOpen, onClose, onSave, productId, personaId, availableFeatures = [], allEpics = [], onFeatureCreated }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [activeTab, setActiveTab] = useState('link');
  const [selectedFeatureId, setSelectedFeatureId] = useState('');
  const [epicFilterInModal, setEpicFilterInModal] = useState('all');
  const [saving, setSaving] = useState(false);
  
  // Create new feature form
  const [newFeatureTitle, setNewFeatureTitle] = useState('');
  const [newFeatureDescription, setNewFeatureDescription] = useState('');
  const [selectedEpicId, setSelectedEpicId] = useState('');
  
  // Create new epic form (inline modal)
  const [showCreateEpic, setShowCreateEpic] = useState(false);
  const [newEpicTitle, setNewEpicTitle] = useState('');
  const [newEpicDescription, setNewEpicDescription] = useState('');
  const [creatingEpic, setCreatingEpic] = useState(false);
  const [localEpics, setLocalEpics] = useState([]);
  
  // Combine passed epics with any locally created ones
  const combinedEpics = React.useMemo(() => {
    return [...allEpics, ...localEpics];
  }, [allEpics, localEpics]);
  
  // Get epic name for a feature
  const getEpicName = (epicId) => {
    if (!epicId) return null;
    const epic = combinedEpics.find(e => e.epic_id === epicId);
    return epic?.title || null;
  };
  
  // Build epic filter options for the modal
  const epicFilterOptionsInModal = React.useMemo(() => {
    const epicCounts = {};
    availableFeatures.forEach(f => {
      const epicId = f.epic_id || 'none';
      epicCounts[epicId] = (epicCounts[epicId] || 0) + 1;
    });
    
    const options = [{ value: 'all', label: `All Epics (${availableFeatures.length})` }];
    
    allEpics.forEach(epic => {
      const count = epicCounts[epic.epic_id] || 0;
      if (count > 0) {
        options.push({ value: epic.epic_id, label: `${epic.title} (${count})` });
      }
    });
    
    // Add "No Epic" option if there are features without epic
    const noEpicCount = epicCounts['none'] || 0;
    if (noEpicCount > 0) {
      options.push({ value: 'none', label: `No Epic (${noEpicCount})` });
    }
    
    return options;
  }, [availableFeatures, allEpics]);
  
  // Filtered features based on epic selection in modal
  const filteredAvailableFeatures = React.useMemo(() => {
    if (epicFilterInModal === 'all') return availableFeatures;
    if (epicFilterInModal === 'none') return availableFeatures.filter(f => !f.epic_id);
    return availableFeatures.filter(f => f.epic_id === epicFilterInModal);
  }, [availableFeatures, epicFilterInModal]);
  
  const selectedFeature = availableFeatures.find(f => f.feature_id === selectedFeatureId);
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedFeatureId('');
      setNewFeatureTitle('');
      setNewFeatureDescription('');
      setSelectedEpicId('');
      setActiveTab('link');
      setEpicFilterInModal('all');
      setShowCreateEpic(false);
      setNewEpicTitle('');
      setNewEpicDescription('');
      setLocalEpics([]);
    }
  }, [isOpen]);
  
  // Link existing feature to persona
  const handleLink = async () => {
    if (!selectedFeatureId || !personaId) return;
    
    setSaving(true);
    try {
      // Get current feature data
      const featureRes = await featuresApi.get(selectedFeatureId);
      const feature = featureRes.data;
      
      // Add persona to feature's persona_ids array
      const currentPersonaIds = feature.persona_ids || [];
      if (!currentPersonaIds.includes(personaId)) {
        await featuresApi.update(selectedFeatureId, {
          persona_ids: [...currentPersonaIds, personaId]
        });
      }
      
      toast.success('Feature linked to persona');
      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to link feature:', err);
      toast.error('Failed to link feature');
    } finally {
      setSaving(false);
    }
  };
  
  // Create new epic
  const handleCreateEpic = async () => {
    if (!newEpicTitle.trim()) return;
    
    setCreatingEpic(true);
    try {
      const res = await epicsApi.createForProduct(productId, {
        title: newEpicTitle.trim(),
        description: newEpicDescription.trim(),
        status: 'Backlog'
      });
      
      const newEpic = res.data;
      setLocalEpics(prev => [...prev, newEpic]);
      setSelectedEpicId(newEpic.epic_id);
      setShowCreateEpic(false);
      setNewEpicTitle('');
      setNewEpicDescription('');
      toast.success('Epic created');
    } catch (err) {
      console.error('Failed to create epic:', err);
      toast.error('Failed to create epic');
    } finally {
      setCreatingEpic(false);
    }
  };
  
  // Create new feature and link to persona
  const handleCreate = async () => {
    if (!newFeatureTitle.trim() || !personaId || !selectedEpicId) return;
    
    setSaving(true);
    try {
      const featureData = {
        title: newFeatureTitle.trim(),
        description: newFeatureDescription.trim(),
        status: 'Backlog',
        persona_ids: [personaId],
        epic_id: selectedEpicId
      };
      
      await featuresApi.createForProduct(productId, featureData);
      toast.success('Feature created and linked to persona');
      
      // Call onFeatureCreated with the epic ID so parent can set filter
      if (onFeatureCreated) {
        onFeatureCreated(selectedEpicId);
      }
      
      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to create feature:', err);
      toast.error('Failed to create feature');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={cn("relative z-10 p-6 rounded-lg shadow-xl max-w-lg w-full mx-4", colors.bgCard)}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={cn("text-lg font-semibold flex items-center gap-2", colors.textPrimary)}>
            <Package className="w-5 h-5" />
            Add Feature
          </h3>
          <button
            onClick={onClose}
            className={cn("p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700", colors.textMuted)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Tabs */}
        <div className={cn("flex gap-1 mb-4 p-1 rounded-lg", colors.bgTertiary)}>
          <button
            onClick={() => setActiveTab('link')}
            className={cn(
              "flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors",
              activeTab === 'link' 
                ? cn(colors.bgCard, colors.textPrimary, "shadow-sm")
                : colors.textMuted
            )}
          >
            Link Existing
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={cn(
              "flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors",
              activeTab === 'create' 
                ? cn(colors.bgCard, colors.textPrimary, "shadow-sm")
                : colors.textMuted
            )}
          >
            Create New
          </button>
        </div>
        
        {/* Link Existing Tab */}
        {activeTab === 'link' && (
          <>
            {availableFeatures.length === 0 ? (
              <div className={cn("text-center py-8", colors.textMuted)}>
                <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">All features are already linked to this persona</p>
                <p className="text-xs mt-1">Or no features exist yet in this product</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Epic Filter */}
                {epicFilterOptionsInModal.length > 1 && (
                  <div>
                    <Label className={colors.textSecondary}>Filter by Epic</Label>
                    <Select value={epicFilterInModal} onValueChange={(val) => {
                      setEpicFilterInModal(val);
                      setSelectedFeatureId(''); // Reset feature selection when filter changes
                    }}>
                      <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={cn("z-[70]", colors.bgCard, colors.borderPrimary)}>
                        {epicFilterOptionsInModal.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Feature Selection */}
                <div>
                  <Label className={colors.textSecondary}>Select Feature ({filteredAvailableFeatures.length} available)</Label>
                  <Select value={selectedFeatureId} onValueChange={setSelectedFeatureId}>
                    <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}>
                      <SelectValue placeholder="Choose a feature to link..." />
                    </SelectTrigger>
                    <SelectContent className={cn("z-[70] max-h-60 w-[400px]", colors.bgCard, colors.borderPrimary)}>
                      {filteredAvailableFeatures.map(feature => (
                        <SelectItem key={feature.feature_id} value={feature.feature_id}>
                          <div className="flex items-center gap-2">
                            <span className={cn(
                              "text-xs px-1.5 py-0.5 rounded flex-shrink-0",
                              STATUS_COLORS[feature.status] || STATUS_COLORS['Backlog']
                            )}>
                              {feature.status || 'Backlog'}
                            </span>
                            <span className="truncate">{feature.title}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Feature Preview */}
                {selectedFeature && (
                  <div className={cn("p-3 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                    {getEpicName(selectedFeature.epic_id) && (
                      <p className={cn("text-xs mb-2", colors.textMuted)}>
                        Epic: {getEpicName(selectedFeature.epic_id)}
                      </p>
                    )}
                    <Label className={cn("text-xs mb-1 block", colors.textMuted)}>Description</Label>
                    <p className={cn("text-sm", colors.textSecondary)}>
                      {selectedFeature.description || 'No description provided'}
                    </p>
                  </div>
                )}
                
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={onClose} disabled={saving}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleLink}
                    disabled={!selectedFeatureId || saving}
                    className={cn(colors.accentGradient)}
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                    Link Feature
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
        
        {/* Create New Tab */}
        {activeTab === 'create' && (
          <div className="space-y-4">
            <div>
              <Label className={colors.textSecondary}>Feature Title *</Label>
              <Input
                value={newFeatureTitle}
                onChange={(e) => setNewFeatureTitle(e.target.value)}
                placeholder="e.g., User Authentication"
                className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
              />
            </div>
            
            <div>
              <Label className={colors.textSecondary}>Description</Label>
              <Textarea
                value={newFeatureDescription}
                onChange={(e) => setNewFeatureDescription(e.target.value)}
                placeholder="Describe the feature's purpose..."
                rows={3}
                className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
              />
            </div>
            
            {/* Epic Selection - Required */}
            <div>
              <div className="flex items-center justify-between">
                <Label className={colors.textSecondary}>Epic *</Label>
                <button
                  type="button"
                  onClick={() => setShowCreateEpic(true)}
                  className={cn("text-xs text-purple-500 hover:text-purple-600 hover:underline")}
                >
                  + Create New Epic
                </button>
              </div>
              <Select value={selectedEpicId} onValueChange={setSelectedEpicId}>
                <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}>
                  <SelectValue placeholder="Select an epic..." />
                </SelectTrigger>
                <SelectContent className={cn("z-[75] max-h-60", colors.bgCard, colors.borderPrimary)}>
                  {combinedEpics.map(epic => (
                    <SelectItem key={epic.epic_id} value={epic.epic_id}>
                      {epic.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {combinedEpics.length === 0 && (
                <p className={cn("text-xs mt-1", colors.textMuted)}>
                  No epics available. Create one using the link above.
                </p>
              )}
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreate}
                disabled={!newFeatureTitle.trim() || !selectedEpicId || saving}
                className={cn(colors.accentGradient)}
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                Create Feature
              </Button>
            </div>
          </div>
        )}
        
        {/* Create Epic Inline Modal */}
        {showCreateEpic && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowCreateEpic(false)} />
            <div className={cn("relative z-10 p-5 rounded-lg shadow-xl max-w-md w-full mx-4", colors.bgCard)}>
              <h4 className={cn("text-base font-semibold mb-4", colors.textPrimary)}>Create New Epic</h4>
              
              <div className="space-y-3">
                <div>
                  <Label className={colors.textSecondary}>Epic Title *</Label>
                  <Input
                    value={newEpicTitle}
                    onChange={(e) => setNewEpicTitle(e.target.value)}
                    placeholder="e.g., User Management"
                    className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                    autoFocus
                  />
                </div>
                
                <div>
                  <Label className={colors.textSecondary}>Description</Label>
                  <Textarea
                    value={newEpicDescription}
                    onChange={(e) => setNewEpicDescription(e.target.value)}
                    placeholder="Brief description of this epic..."
                    rows={2}
                    className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => setShowCreateEpic(false)} disabled={creatingEpic}>
                  Cancel
                </Button>
                <Button 
                  size="sm"
                  onClick={handleCreateEpic}
                  disabled={!newEpicTitle.trim() || creatingEpic}
                  className={cn(colors.accentGradient)}
                >
                  {creatingEpic ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                  Create Epic
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Add Epic Modal - supports linking existing or creating new
const AddEpicModal = ({ isOpen, onClose, onSave, productId, personaId, availableEpics = [] }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [activeTab, setActiveTab] = useState('link');
  const [selectedEpicId, setSelectedEpicId] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Create new epic form
  const [newEpicTitle, setNewEpicTitle] = useState('');
  const [newEpicDescription, setNewEpicDescription] = useState('');
  
  const selectedEpic = availableEpics.find(e => e.epic_id === selectedEpicId);
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedEpicId('');
      setNewEpicTitle('');
      setNewEpicDescription('');
      setActiveTab('link');
    }
  }, [isOpen]);
  
  // Link existing epic to persona
  const handleLink = async () => {
    if (!selectedEpicId || !personaId) return;
    
    setSaving(true);
    try {
      // Get current epic data
      const epicRes = await epicsApi.get(selectedEpicId);
      const epic = epicRes.data;
      
      // Add persona to epic's persona_ids array
      const currentPersonaIds = epic.persona_ids || [];
      if (!currentPersonaIds.includes(personaId)) {
        await epicsApi.update(selectedEpicId, {
          persona_ids: [...currentPersonaIds, personaId]
        });
      }
      
      toast.success('Epic linked to persona');
      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to link epic:', err);
      toast.error('Failed to link epic');
    } finally {
      setSaving(false);
    }
  };
  
  // Create new epic and link to persona
  const handleCreate = async () => {
    if (!newEpicTitle.trim() || !personaId) return;
    
    setSaving(true);
    try {
      const epicData = {
        title: newEpicTitle.trim(),
        description: newEpicDescription.trim(),
        status: 'Backlog',
        persona_ids: [personaId]
      };
      
      await epicsApi.createForProduct(productId, epicData);
      toast.success('Epic created and linked to persona');
      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to create epic:', err);
      toast.error('Failed to create epic');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={cn("relative z-10 p-6 rounded-lg shadow-xl max-w-lg w-full mx-4", colors.bgCard)}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={cn("text-lg font-semibold flex items-center gap-2", colors.textPrimary)}>
            <Layers className="w-5 h-5" />
            Add Epic
          </h3>
          <button
            onClick={onClose}
            className={cn("p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700", colors.textMuted)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Tabs */}
        <div className={cn("flex gap-1 mb-4 p-1 rounded-lg", colors.bgTertiary)}>
          <button
            onClick={() => setActiveTab('link')}
            className={cn(
              "flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors",
              activeTab === 'link' 
                ? cn(colors.bgCard, colors.textPrimary, "shadow-sm")
                : colors.textMuted
            )}
          >
            Link Existing
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={cn(
              "flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors",
              activeTab === 'create' 
                ? cn(colors.bgCard, colors.textPrimary, "shadow-sm")
                : colors.textMuted
            )}
          >
            Create New
          </button>
        </div>
        
        {/* Link Existing Tab */}
        {activeTab === 'link' && (
          <>
            {availableEpics.length === 0 ? (
              <div className={cn("text-center py-8", colors.textMuted)}>
                <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">All epics are already linked to this persona</p>
                <p className="text-xs mt-1">Or no epics exist yet in this product</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Epic Selection */}
                <div>
                  <Label className={colors.textSecondary}>Select Epic ({availableEpics.length} available)</Label>
                  <Select value={selectedEpicId} onValueChange={setSelectedEpicId}>
                    <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}>
                      <SelectValue placeholder="Choose an epic to link..." />
                    </SelectTrigger>
                    <SelectContent className={cn("z-[70] max-h-60 w-[400px]", colors.bgCard, colors.borderPrimary)}>
                      {availableEpics.map(epic => (
                        <SelectItem key={epic.epic_id} value={epic.epic_id}>
                          <div className="flex items-center gap-2">
                            <span className={cn(
                              "text-xs px-1.5 py-0.5 rounded flex-shrink-0",
                              STATUS_COLORS[epic.status] || STATUS_COLORS['Backlog']
                            )}>
                              {epic.status || 'Backlog'}
                            </span>
                            <span className="truncate">{epic.title}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Epic Preview */}
                {selectedEpic && (
                  <div className={cn("p-3 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                    <Label className={cn("text-xs mb-1 block", colors.textMuted)}>Description</Label>
                    <p className={cn("text-sm", colors.textSecondary)}>
                      {selectedEpic.description || 'No description provided'}
                    </p>
                  </div>
                )}
                
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={onClose} disabled={saving}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleLink}
                    disabled={!selectedEpicId || saving}
                    className={cn(colors.accentGradient)}
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                    Link Epic
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
        
        {/* Create New Tab */}
        {activeTab === 'create' && (
          <div className="space-y-4">
            <div>
              <Label className={colors.textSecondary}>Epic Title *</Label>
              <Input
                value={newEpicTitle}
                onChange={(e) => setNewEpicTitle(e.target.value)}
                placeholder="e.g., User Management System"
                className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
              />
            </div>
            
            <div>
              <Label className={colors.textSecondary}>Description</Label>
              <Textarea
                value={newEpicDescription}
                onChange={(e) => setNewEpicDescription(e.target.value)}
                placeholder="Describe the epic's purpose and scope..."
                rows={3}
                className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreate}
                disabled={!newEpicTitle.trim() || saving}
                className={cn(colors.accentGradient)}
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                Create Epic
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * PersonaDetailModal
 * 
 * Displays and allows editing of persona details with linked features.
 * 
 * STANDALONE MODE:
 * When `standalone={true}`, this modal operates independently without requiring
 * parent callbacks. Used by Executive AI to open personas in-place without
 * navigating away. In standalone mode:
 * - onUpdate callback is ignored (API is called directly, toast shown)
 * - Modal closes automatically after save/delete
 * - No parent state refresh needed
 */
const PersonaDetailModal = ({ 
  isOpen,
  persona, 
  onUpdate, 
  onClose, 
  productContext = {},
  allFeatures = [],  // Passed from parent - all features in the product
  allEpics = [],     // Passed from parent - all epics in the product
  onOpenFeature, // Callback to open feature from parent
  standalone = false,  // When true, modal operates independently without parent callbacks
  zIndex = 50,  // Z-index for modal stacking
}) => {
  const { getThemeColors } = useSafeMode();
  const { canCrudProducts, canUseAI } = usePermissions();
  const { user } = useAuth();
  const colors = getThemeColors();
  
  // Permission flags - role-based edit mode
  const canEdit = canCrudProducts;
  const canUseAICopilot = canUseAI;
  
  // Show AI Copilot only for existing personas in edit mode
  const showAICopilot = canUseAICopilot && canEdit && persona?.persona_id;
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    segment_id: '',
    goals: '',
    pain_points: '',
    context: ''
  });
  
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [showAddFeatureModal, setShowAddFeatureModal] = useState(false);
  const [showAddEpicModal, setShowAddEpicModal] = useState(false);
  const [refreshingFeatures, setRefreshingFeatures] = useState(false);
  
  // Pending links from AI Copilot (not saved until form is saved)
  const [pendingFeatureLinks, setPendingFeatureLinks] = useState([]);
  const [pendingEpicLinks, setPendingEpicLinks] = useState([]);
  
  // Optimistic unlink tracking (items being unlinked, hidden immediately)
  const [unlinkingFeatureIds, setUnlinkingFeatureIds] = useState([]);
  const [unlinkingEpicIds, setUnlinkingEpicIds] = useState([]);
  
  // Feature modal state
  const [selectedFeatureForModal, setSelectedFeatureForModal] = useState(null);
  
  // Epic filter state
  const [epicFilter, setEpicFilter] = useState('all');
  
  // Team members for @mentions in NotesPanel
  const [teamMembers, setTeamMembers] = useState([]);
  
  // AI Copilot state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [proposedUpdates, setProposedUpdates] = useState(null);
  const [hasAIActivity, setHasAIActivity] = useState(false);
  const [sessionActions, setSessionActions] = useState([]);
  const [showClearHistoryConfirm, setShowClearHistoryConfirm] = useState(false);
  const chatEndRef = useRef(null);
  const draftSaveTimeoutRef = useRef(null);
  
  // AI personality (mock for now)
  const activePersonality = { 
    name: 'AI Assistant', 
    icon: '🤖' 
  };
  
  // Computed: Features linked to this persona (filtered from allFeatures prop)
  const linkedFeatures = React.useMemo(() => {
    if (!persona?.persona_id) return [];
    return allFeatures.filter(f => f.persona_ids?.includes(persona.persona_id));
  }, [allFeatures, persona?.persona_id]);
  
  // Computed: Features NOT linked to this persona (for Add Feature modal)
  const availableFeaturesForLinking = React.useMemo(() => {
    if (!persona?.persona_id) return allFeatures;
    return allFeatures.filter(f => !f.persona_ids?.includes(persona.persona_id));
  }, [allFeatures, persona?.persona_id]);
  
  // Computed: Pending feature data (full feature objects for display)
  const pendingFeatureData = React.useMemo(() => {
    return pendingFeatureLinks
      .map(featureId => allFeatures.find(f => f.feature_id === featureId))
      .filter(Boolean);
  }, [pendingFeatureLinks, allFeatures]);
  
  // Computed: Pending epic data (full epic objects for display)
  const pendingEpicData = React.useMemo(() => {
    return pendingEpicLinks
      .map(epicId => allEpics.find(e => e.epic_id === epicId))
      .filter(Boolean);
  }, [pendingEpicLinks, allEpics]);
  
  // Computed: Epics that have this persona linked
  const linkedEpics = React.useMemo(() => {
    if (!persona?.persona_id) return [];
    return allEpics.filter(e => e.persona_ids?.includes(persona.persona_id));
  }, [allEpics, persona?.persona_id]);
  
  // Computed: Epics NOT linked to this persona (for Add Epic modal)
  const availableEpicsForLinking = React.useMemo(() => {
    if (!persona?.persona_id) return allEpics;
    return allEpics.filter(e => !e.persona_ids?.includes(persona.persona_id));
  }, [allEpics, persona?.persona_id]);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');
  
  // Computed: Epic filter options with feature counts
  const epicFilterOptions = React.useMemo(() => {
    const epicCounts = {};
    linkedFeatures.forEach(f => {
      const epicId = f.epic_id || 'none';
      epicCounts[epicId] = (epicCounts[epicId] || 0) + 1;
    });
    
    const options = [{ value: 'all', label: `All Epics (${linkedFeatures.length})` }];
    
    allEpics.forEach(epic => {
      const count = epicCounts[epic.epic_id] || 0;
      if (count > 0) {
        options.push({ value: epic.epic_id, label: `${epic.title} (${count})` });
      }
    });
    
    // Add "No Epic" option if there are features without epic
    const noEpicCount = epicCounts['none'] || 0;
    if (noEpicCount > 0) {
      options.push({ value: 'none', label: `No Epic (${noEpicCount})` });
    }
    
    return options;
  }, [linkedFeatures, allEpics]);
  
  // Filtered features based on epic selection (excluding items being optimistically unlinked)
  const filteredFeatures = React.useMemo(() => {
    let features = linkedFeatures.filter(f => !unlinkingFeatureIds.includes(f.feature_id));
    if (epicFilter === 'all') return features;
    if (epicFilter === 'none') return features.filter(f => !f.epic_id);
    return features.filter(f => f.epic_id === epicFilter);
  }, [linkedFeatures, epicFilter, unlinkingFeatureIds]);
  
  // Filtered linked epics (excluding items being optimistically unlinked)
  const filteredLinkedEpics = React.useMemo(() => {
    return linkedEpics.filter(e => !unlinkingEpicIds.includes(e.epic_id));
  }, [linkedEpics, unlinkingEpicIds]);
  
  // Handle AI Co-Pilot apply updates
  const handleApplyUpdates = useCallback((updates) => {
    if (!updates) return;
    
    Object.entries(updates).forEach(([field, value]) => {
      // Handle pending feature links from AI (source is feature, target is persona)
      if (field === '_link_feature_ids' && Array.isArray(value)) {
        setPendingFeatureLinks(prev => {
          const newLinks = value.filter(id => !prev.includes(id));
          return [...prev, ...newLinks];
        });
        setHasChanges(true);
        return;
      }
      
      // Handle pending epic links from AI (source is epic, target is persona)
      if (field === '_link_epic_ids' && Array.isArray(value)) {
        setPendingEpicLinks(prev => {
          const newLinks = value.filter(id => !prev.includes(id));
          return [...prev, ...newLinks];
        });
        setHasChanges(true);
        return;
      }
      
      // Handle unlink feature (remove from pending if present)
      if (field === '_unlink_feature_ids' && Array.isArray(value)) {
        setPendingFeatureLinks(prev => prev.filter(id => !value.includes(id)));
        setHasChanges(true);
        return;
      }
      
      // Handle unlink epic (remove from pending if present)
      if (field === '_unlink_epic_ids' && Array.isArray(value)) {
        setPendingEpicLinks(prev => prev.filter(id => !value.includes(id)));
        setHasChanges(true);
        return;
      }
      
      // Only update known fields
      const validFields = ['name', 'role', 'segment_id', 'goals', 'pain_points', 'context'];
      if (validFields.includes(field)) {
        setFormData(prev => ({ ...prev, [field]: value }));
      }
    });
    
    setHasChanges(true);
  }, []);
  
  // Refresh persona data after changes
  const refreshPersonaData = useCallback(async () => {
    if (!persona?.persona_id) return;
    try {
      const res = await personasApi.get(persona.persona_id);
      if (res.data) {
        // Validate segment_id exists in market_segments, otherwise treat as no segment
        const validSegmentId = res.data.segment_id && 
          productContext?.market_segments?.some(s => s.segment_id === res.data.segment_id)
            ? res.data.segment_id 
            : '';
        setFormData({
          name: res.data.name || '',
          role: res.data.role || '',
          segment_id: validSegmentId,
          goals: res.data.goals || '',
          pain_points: res.data.pain_points || '',
          context: res.data.context || ''
        });
      }
    } catch (err) {
      console.error('Failed to refresh persona:', err);
    }
  }, [persona?.persona_id, productContext?.market_segments]);

  // Initialize form data when persona changes
  useEffect(() => {
    if (persona && isOpen) {
      // Validate segment_id exists in market_segments, otherwise treat as no segment
      const validSegmentId = persona.segment_id && 
        productContext?.market_segments?.some(s => s.segment_id === persona.segment_id)
          ? persona.segment_id 
          : '';
      setFormData({
        name: persona.name || '',
        role: persona.role || '',
        segment_id: validSegmentId,
        goals: persona.goals || '',
        pain_points: persona.pain_points || '',
        context: persona.context || ''
      });
      setHasChanges(false);
      setActiveTab('overview');
      setEpicFilter('all');
      // Reset pending links when persona changes
      setPendingFeatureLinks([]);
      setPendingEpicLinks([]);
      // Reset optimistic unlink tracking
      setUnlinkingFeatureIds([]);
      setUnlinkingEpicIds([]);
    }
  }, [persona, isOpen]);

  // Load team members for @mentions
  useEffect(() => {
    const loadTeam = async () => {
      try {
        const res = await teamApi.list();
        const activeMembers = (res.data?.members || []).filter(m => m.status === 'active');
        setTeamMembers(activeMembers);
      } catch (err) {
        console.error('Failed to load team:', err);
      }
    };
    
    if (isOpen) {
      loadTeam();
    }
  }, [isOpen]);

  // Helper functions for AI copilot
  const saveDraft = (personaId, content) => {
    // Mock implementation - could save to localStorage
    localStorage.setItem(`persona_draft_${personaId}`, content);
  };
  
  const loadDraft = (personaId) => {
    // Mock implementation - could load from localStorage
    return localStorage.getItem(`persona_draft_${personaId}`) || '';
  };
  
  const clearDraft = (personaId) => {
    localStorage.removeItem(`persona_draft_${personaId}`);
  };
  
  const handleChatInputChange = (e) => {
    setChatInput(e.target.value);
    // Auto-save draft
    if (persona?.persona_id) {
      if (draftSaveTimeoutRef.current) {
        clearTimeout(draftSaveTimeoutRef.current);
      }
      draftSaveTimeoutRef.current = setTimeout(() => {
        saveDraft(persona.persona_id, e.target.value);
      }, 1000);
    }
  };
  
  const buildPersonaContext = (personaData) => {
    return `=== PERSONA DETAILS ===
Name: ${personaData.name || 'Not defined'}
Role: ${personaData.role || 'Not defined'}
Goals: ${personaData.goals || 'Not defined'}
Pain Points: ${personaData.pain_points || 'Not defined'}
Context/Background: ${personaData.context || 'Not defined'}

=== LINKED FEATURES ===
Features for this persona: ${linkedFeatures.length > 0 ? linkedFeatures.map(f => f.title).join(', ') : 'None'}`;
  };
  
  const getSystemPrompt = () => {
    return `You are an AI assistant helping to refine user personas for product development. 
    
Your role is to:
1. Analyze persona completeness and quality
2. Suggest improvements to goals, pain points, and context
3. Recommend features that would serve this persona
4. Help identify gaps in persona definition

When suggesting updates to persona fields, format your response with an AI_ACTION block:
---AI_ACTION---
type: APPLY_UPDATES
data: {
  "field_name": "new_value",
  "another_field": "another_value"
}
---END_ACTION---

Valid field names are: name, role, goals, pain_points, context

Be concise, actionable, and focus on improving the persona's usefulness for product decisions.`;
  };
  
  // Helper to extract action data from shared parseAIResponse format
  const extractActionData = (response) => {
    const { displayMessage, actionData } = parseAIResponse(response);
    return { displayMessage, actionData };
  };
  
  const handleAIAction = (actionData) => {
    if (!actionData) return;
    
    let newUpdates = null;
    
    switch (actionData.type) {
      case 'APPLY_UPDATES':
        if (actionData.data && Object.keys(actionData.data).length > 0) {
          newUpdates = actionData.data;
          setProposedUpdates(newUpdates);
        }
        break;
        
      default:
        setProposedUpdates(null);
        break;
    }
    
    // Save pending actions to backend
    if (persona?.persona_id && newUpdates) {
      unifiedCopilotApi.savePendingActions('persona', persona.persona_id, { proposed_updates: newUpdates }).catch(() => {});
    }
  };
  
  const getInitialAssessment = async (personaData) => {
    setChatLoading(true);
    setHasAIActivity(true);
    try {
      // Use unified co-pilot endpoint
      const res = await unifiedCopilotApi.chat('persona', persona?.persona_id, {
        message: 'Give me a quick assessment of this persona. Is it well-defined? What improvements would you suggest?',
        conversation_history: []
      });
      
      const aiResponse = res.data.response || "I couldn't assess this persona.";
      const { displayMessage, actionData } = extractActionData(aiResponse);
      
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: displayMessage
      }]);
      
      // Save AI response to team history
      if (persona?.persona_id) {
        unifiedCopilotApi.addMessage('persona', persona.persona_id, 'assistant', displayMessage).catch(err => {
          console.error('Failed to save AI response:', err);
        });
      }
      
      if (actionData) {
        handleAIAction(actionData);
      }
    } catch (err) {
      console.error('Assessment error:', err);
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `Sorry, I couldn't complete the assessment. Please try again or ask me a specific question!`
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleChange = (field, value) => {
    console.log(`[PersonaDetailModal] handleChange: ${field} =`, value?.substring?.(0, 50) || value);
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };
  
  const handleSave = async () => {
    if (!persona?.persona_id) return;
    
    console.log('[PersonaDetailModal] Saving formData:', JSON.stringify(formData, null, 2));
    console.log('[PersonaDetailModal] Pending feature links:', pendingFeatureLinks);
    console.log('[PersonaDetailModal] Pending epic links:', pendingEpicLinks);
    
    setSaving(true);
    try {
      // Include pending links in the update payload
      const updatePayload = {
        ...formData,
        pending_feature_links: pendingFeatureLinks.length > 0 ? pendingFeatureLinks : undefined,
        pending_epic_links: pendingEpicLinks.length > 0 ? pendingEpicLinks : undefined
      };
      
      await personasApi.update(persona.persona_id, updatePayload);
      toast.success('Persona saved');
      setHasChanges(false);
      
      // STANDALONE MODE: Skip parent callback, just show toast
      if (!standalone && onUpdate) {
        await onUpdate();
      }
      
      // Clear pending links AFTER parent data refreshes so UI shows linked items first
      setPendingFeatureLinks([]);
      setPendingEpicLinks([]);
    } catch (err) {
      toast.error('Failed to save persona');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndClose = async () => {
    if (!persona?.persona_id) return;
    
    setSaving(true);
    try {
      // Include pending links in the update payload
      const updatePayload = {
        ...formData,
        pending_feature_links: pendingFeatureLinks.length > 0 ? pendingFeatureLinks : undefined,
        pending_epic_links: pendingEpicLinks.length > 0 ? pendingEpicLinks : undefined
      };
      
      await personasApi.update(persona.persona_id, updatePayload);
      toast.success('Persona saved');
      setHasChanges(false);
      
      // STANDALONE MODE: Skip parent callback, just show toast and close
      if (!standalone && onUpdate) {
        await onUpdate();
      }
      
      // Clear pending links AFTER parent data refreshes
      setPendingFeatureLinks([]);
      setPendingEpicLinks([]);
      
      performClose();
    } catch (err) {
      toast.error('Failed to save persona');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };
  
  // Delete persona
  const handleDelete = async () => {
    if (!persona?.persona_id) return;
    
    setDeleting(true);
    try {
      await personasApi.delete(persona.persona_id);
      toast.success('Persona deleted');
      setShowDeleteConfirm(false);
      
      // STANDALONE MODE: Skip parent callback
      if (!standalone) {
        onUpdate?.();
      }
      onClose();
    } catch (err) {
      toast.error('Failed to delete persona');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };
  
  // Unlink feature from persona
  const handleUnlinkFeature = async (feature) => {
    // Optimistic: hide immediately
    setUnlinkingFeatureIds(prev => [...prev, feature.feature_id]);
    
    try {
      const updatedPersonaIds = (feature.persona_ids || []).filter(id => id !== persona.persona_id);
      await featuresApi.update(feature.feature_id, { persona_ids: updatedPersonaIds });
      toast.success('Feature unlinked');
      
      // STANDALONE MODE: Skip parent callback
      if (!standalone) {
        onUpdate?.(); // Refresh parent data to update allFeatures
      }
    } catch (err) {
      // Restore on failure
      setUnlinkingFeatureIds(prev => prev.filter(id => id !== feature.feature_id));
      toast.error('Failed to unlink feature');
      console.error(err);
    }
  };
  
  const handleUnlinkEpic = async (epic) => {
    // Optimistic: hide immediately
    setUnlinkingEpicIds(prev => [...prev, epic.epic_id]);
    
    try {
      const updatedPersonaIds = (epic.persona_ids || []).filter(id => id !== persona.persona_id);
      await epicsApi.update(epic.epic_id, { persona_ids: updatedPersonaIds });
      toast.success('Epic unlinked');
      
      // STANDALONE MODE: Skip parent callback
      if (!standalone) {
        onUpdate?.(); // Refresh parent data to update allEpics
      }
    } catch (err) {
      // Restore on failure
      setUnlinkingEpicIds(prev => prev.filter(id => id !== epic.epic_id));
      toast.error('Failed to unlink epic');
      console.error(err);
    }
  };
  
  // Apply proposed updates from AI
  const applyProposedUpdates = () => {
    if (!proposedUpdates) return;
    
    console.log('[PersonaDetailModal] Raw proposedUpdates:', JSON.stringify(proposedUpdates, null, 2));
    
    setHasChanges(true);
    const appliedFields = [];
    
    const fieldLabels = {
      name: 'Name', role: 'Role', goals: 'Goals',
      pain_points: 'Pain Points', context: 'Context/Background'
    };
    
    // Valid persona form fields
    const validFields = ['name', 'role', 'goals', 'pain_points', 'context'];
    
    Object.entries(proposedUpdates).forEach(([key, value]) => {
      console.log(`[PersonaDetailModal] Processing field: "${key}"`);
      
      // Check if it's a valid persona field
      if (validFields.includes(key)) {
        handleChange(key, value);
        appliedFields.push(fieldLabels[key] || key);
        console.log(`[PersonaDetailModal] Applied "${key}" with value length: ${value?.length || 0}`);
      } else {
        console.log(`[PersonaDetailModal] Skipped unknown field: "${key}"`);
      }
    });
    
    // Add confirmation message to chat
    const appliedMessage = appliedFields.length > 0
      ? `✅ **Suggestions Applied!**\n\nUpdated: ${appliedFields.join(', ')}\n\nRemember to click **Save** to persist your changes.`
      : `⚠️ **No fields could be applied.**\n\nThe AI response used field names that couldn't be mapped. Expected: name, role, goals, pain_points, context`;
    
    setChatMessages(prev => [...prev, {
      role: 'assistant',
      content: appliedMessage
    }]);
    
    // Save the applied message to backend chat history
    if (persona?.persona_id) {
      unifiedCopilotApi.addMessage('persona', persona.persona_id, 'assistant', appliedMessage).catch(err => {
        console.error('Failed to save applied message to chat history:', err);
      });
    }
    
    // Track action
    setSessionActions(prev => [...prev, ...appliedFields.map(f => `applied_${f.toLowerCase().replace(' ', '_').replace('/', '_')}`)]);
    
    if (appliedFields.length > 0) {
      toast.success('Updates applied to form!');
    } else {
      toast.error('Could not apply updates - check console for details');
    }
    setProposedUpdates(null);
    
    // Clear pending actions from backend
    if (persona?.persona_id) {
      unifiedCopilotApi.clearPendingActions('persona', persona.persona_id).catch(() => {});
    }
  };
  
  // AI Chat
  const handleSendChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = chatInput.trim();
    setChatInput('');
    clearDraft(persona?.persona_id);
    
    const historyForAPI = chatMessages
      .filter(msg => msg.role !== 'system')
      .slice(-10)
      .map(msg => ({
        role: msg.role,
        content: msg.content
      }));
    
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);
    
    // Save user message to team history
    if (persona?.persona_id) {
      unifiedCopilotApi.addMessage('persona', persona.persona_id, 'user', userMessage).catch(err => {
        console.error('Failed to save user message:', err);
      });
    }
    
    try {
      // Use unified co-pilot endpoint
      const res = await unifiedCopilotApi.chat('persona', persona?.persona_id, {
        message: userMessage,
        conversation_history: historyForAPI
      });
      
      const aiResponse = res.data.response || "I couldn't generate a response.";
      const { displayMessage, actionData } = extractActionData(aiResponse);
      
      setChatMessages(prev => [...prev, { role: 'assistant', content: displayMessage }]);
      setHasAIActivity(true);
      
      // Save AI response to team history
      if (persona?.persona_id) {
        unifiedCopilotApi.addMessage('persona', persona.persona_id, 'assistant', displayMessage).catch(err => {
          console.error('Failed to save AI response:', err);
        });
      }
      
      if (actionData) {
        handleAIAction(actionData);
      }
    } catch (err) {
      setChatMessages(prev => prev.filter((msg, idx) => 
        !(idx === prev.length - 1 && msg.role === 'user' && msg.content === userMessage)
      ));
      setChatInput(userMessage);
      toast.error('AI request failed - your message has been restored. Please try again.', {
        duration: 4000
      });
    } finally {
      setChatLoading(false);
    }
  };
  
  // Handle modal close - check for unsaved changes first
  const handleModalClose = () => {
    // First check for unsaved form changes
    if (hasChanges) {
      setShowUnsavedWarning(true);
      return;
    }
    // No longer need close guard - pending actions persist in chat log
    performClose();
  };
  
  const performClose = () => {
    const shouldLog = hasAIActivity && persona?.persona_id;
    const conversationForSummary = chatMessages.filter((msg, idx) => idx > 0);
    const personaId = persona?.persona_id;
    const actions = [...sessionActions];
    
    // Cancel any pending draft save
    if (draftSaveTimeoutRef.current) {
      clearTimeout(draftSaveTimeoutRef.current);
    }
    // Save any pending draft immediately before closing
    if (chatInput.trim() && personaId) {
      saveDraft(personaId, chatInput);
    }
    
    onClose();
    
    // Process logging in background if needed
    if (shouldLog && conversationForSummary.length >= 2) {
      setTimeout(() => {
        copilotApi.summarizeConversation({
          conversation: conversationForSummary,
          actions_taken: actions
        }).then(res => {
          if (res.data?.is_substantive && res.data?.summary) {
            // Use unified copilot API for conversation logging
            return unifiedCopilotApi.addSummary('persona', personaId, res.data.summary, actions);
          }
        }).then(() => {
          console.log('Conversation logged successfully');
        }).catch(err => {
          console.error('Failed to log conversation:', err);
        });
      }, 100);
    }
  };
  
  // Clear chat history
  const handleClearHistory = async () => {
    if (!persona?.persona_id) return;
    
    try {
      await unifiedCopilotApi.clearHistory('persona', persona.persona_id);
      setChatMessages([{
        role: 'assistant',
        content: `${activePersonality.icon} Chat history cleared. How can I help you with "${persona.name}"?`
      }]);
      setProposedUpdates(null);
      setShowClearHistoryConfirm(false);
      toast.success('Chat history cleared');
    } catch (err) {
      toast.error('Failed to clear chat history');
      console.error(err);
    }
  };
  
  // Handle feature click - open feature modal
  const handleFeatureClick = (feature) => {
    if (onOpenFeature) {
      onOpenFeature(feature);
    } else {
      setSelectedFeatureForModal(feature);
    }
  };
  
  if (!isOpen || !persona) {
    return null;
  }
  
  return (
    <>
      <ModalZIndexProvider zIndex={zIndex}>
      <div className="fixed inset-0" style={{ zIndex }}>
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60" />
        
        {/* Modal */}
        <div className={cn("absolute inset-4 md:inset-8 lg:inset-12 rounded-xl shadow-2xl flex flex-col overflow-hidden", colors.bgCard)}>
          {/* Header */}
          <div className={cn("flex items-center justify-between px-6 py-4 border-b", colors.borderPrimary)}>
            <div className="flex items-center gap-3">
              <div className={cn("w-10 h-10 rounded-full flex items-center justify-center", "bg-purple-100 dark:bg-purple-900/30")}>
                <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h2 className={cn("text-xl font-semibold", colors.textPrimary)}>
                  {canEdit ? 'Edit Persona' : 'View Persona'}
                </h2>
                <p className={cn("text-sm truncate max-w-[400px]", colors.textMuted)}>{formData.name || persona.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {canEdit && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                  title="Delete persona"
                  data-testid="persona-delete-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
              {canEdit && (
                <>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saving || !hasChanges}
                    variant={hasChanges ? "default" : "outline"}
                    className={cn(hasChanges ? colors.accentGradient : "opacity-50 cursor-not-allowed")}
                    data-testid="persona-save-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span className="ml-1">Save</span>
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveAndClose}
                    disabled={saving || !hasChanges}
                    variant={hasChanges ? "default" : "outline"}
                    className={cn(hasChanges ? colors.accentGradient : "opacity-50 cursor-not-allowed")}
                    data-testid="persona-save-close-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span className="ml-1">Save & Close</span>
                  </Button>
                </>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={handleModalClose}
                className={cn(colors.textMuted, "hover:bg-gray-200 dark:hover:bg-gray-700")}
                data-testid="persona-close-btn"
              >
                Close
              </Button>
            </div>
          </div>
        
          {/* Content - 60/40 Split */}
          <div className="flex-1 flex overflow-hidden">
            {/* Left: Form Fields / View */}
            <div className={cn(
              "overflow-y-auto flex flex-col",
              (canUseAICopilot && canEdit) ? "w-[60%] border-r" : "w-full",
              colors.borderPrimary
            )}>
              
              {/* Tab Navigation - Pill Style at TOP */}
              <div className="px-6 pt-4">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className={cn("mb-4 border inline-flex", colors.bgCard, colors.borderPrimary)}>
                    <TabsTrigger 
                      value="overview" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                      data-testid="persona-tab-overview"
                    >
                      Overview
                    </TabsTrigger>
                    <TabsTrigger 
                      value="epics" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                      data-testid="persona-tab-epics"
                    >
                      Epics ({filteredLinkedEpics.length + pendingEpicData.length})
                    </TabsTrigger>
                    <TabsTrigger 
                      value="features" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                      data-testid="persona-tab-features"
                    >
                      Features ({filteredFeatures.length + pendingFeatureData.length})
                    </TabsTrigger>
                    <TabsTrigger 
                      value="attachments" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                      data-testid="persona-tab-attachments"
                    >
                      <Paperclip className="w-3.5 h-3.5 mr-1" />
                      Attachments
                    </TabsTrigger>
                    <TabsTrigger 
                      value="notes" 
                      className={cn("data-[state=active]:shadow-sm transition-all text-sm", colors.tabActive)}
                      data-testid="persona-tab-notes"
                    >
                      <MessageSquare className="w-3.5 h-3.5 mr-1" />
                      Notes
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              
              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                
                {/* ===== OVERVIEW TAB ===== */}
                {activeTab === 'overview' && (
                  <>
                    {/* Key Attributes: Name & Role */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className={colors.textSecondary}>Name *</Label>
                        {canEdit ? (
                          <Input
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder="e.g., Enterprise Emma"
                            className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                            data-testid="persona-name-input"
                          />
                        ) : (
                          <p className={cn("mt-1 text-lg font-medium", colors.textPrimary)}>{formData.name || '—'}</p>
                        )}
                      </div>
                      <div>
                        <Label className={colors.textSecondary}>Role *</Label>
                        {canEdit ? (
                          <Input
                            value={formData.role}
                            onChange={(e) => handleChange('role', e.target.value)}
                            placeholder="e.g., IT Administrator at mid-sized B2B company"
                            className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                            data-testid="persona-role-input"
                          />
                        ) : (
                          <p className={cn("mt-1", colors.textSecondary)}>{formData.role || '—'}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Market Segment */}
                    {productContext?.market_segments?.length > 0 && (
                      <div>
                        <Label className={colors.textSecondary}>Market Segment</Label>
                        {canEdit ? (
                          <Select 
                            value={formData.segment_id || '__none__'} 
                            onValueChange={(value) => handleChange('segment_id', value === '__none__' ? '' : value)}
                          >
                            <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)} data-testid="persona-segment-select">
                              <SelectValue placeholder="Select a market segment..." />
                            </SelectTrigger>
                            <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                              <SelectItem value="__none__">No segment</SelectItem>
                              {productContext.market_segments.map(segment => (
                                <SelectItem key={segment.segment_id} value={segment.segment_id}>
                                  {segment.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className={cn("mt-1", colors.textSecondary)}>
                            {productContext.market_segments.find(s => s.segment_id === formData.segment_id)?.title || '—'}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Goals */}
                    <div>
                      <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
                        <Target className="w-3 h-3" /> Goals
                      </Label>
                      {canEdit ? (
                        <Textarea
                          value={formData.goals}
                          onChange={(e) => handleChange('goals', e.target.value)}
                          rows={4}
                          placeholder="What are this persona's primary goals and objectives?"
                          className={cn("mt-1 min-h-[100px]", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                          data-testid="persona-goals-input"
                        />
                      ) : (
                        <div className={cn("mt-1 p-3 rounded-lg whitespace-pre-wrap", colors.bgTertiary, colors.textSecondary)}>
                          {formData.goals || <span className={colors.textMuted}>No goals defined</span>}
                        </div>
                      )}
                    </div>
                    
                    {/* Pain Points */}
                    <div>
                      <Label className={cn("flex items-center gap-1", colors.textSecondary)}>
                        <AlertTriangle className="w-3 h-3" /> Pain Points
                      </Label>
                      {canEdit ? (
                        <Textarea
                          value={formData.pain_points}
                          onChange={(e) => handleChange('pain_points', e.target.value)}
                          rows={4}
                          placeholder="What frustrates this persona? What challenges do they face?"
                          className={cn("mt-1 min-h-[100px]", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                          data-testid="persona-pain-points-input"
                        />
                      ) : (
                        <div className={cn("mt-1 p-3 rounded-lg whitespace-pre-wrap", colors.bgTertiary, colors.textSecondary)}>
                          {formData.pain_points || <span className={colors.textMuted}>No pain points defined</span>}
                        </div>
                      )}
                    </div>
                    
                    {/* Context */}
                    <div>
                      <Label className={colors.textSecondary}>Context / Background</Label>
                      {canEdit ? (
                        <Textarea
                          value={formData.context}
                          onChange={(e) => handleChange('context', e.target.value)}
                          rows={4}
                          placeholder="Additional context about this persona's work environment, company size, etc."
                          className={cn("mt-1 min-h-[100px]", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                          data-testid="persona-context-input"
                        />
                      ) : (
                        <div className={cn("mt-1 p-3 rounded-lg whitespace-pre-wrap", colors.bgTertiary, colors.textSecondary)}>
                          {formData.context || <span className={colors.textMuted}>No context defined</span>}
                        </div>
                      )}
                    </div>
                  </>
                )}
                
                {/* ===== FEATURES TAB ===== */}
                {activeTab === 'features' && (
                  <div className="space-y-4">
                    {/* Header with filter and add button */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Label className={cn("flex items-center gap-2", colors.textSecondary)}>
                          <Package className="w-4 h-4" />
                          Linked Features
                          {refreshingFeatures && (
                            <Loader2 className="w-3 h-3 animate-spin ml-1" />
                          )}
                        </Label>
                        {epicFilterOptions.length > 1 && (
                          <Select value={epicFilter} onValueChange={setEpicFilter}>
                            <SelectTrigger className={cn("w-48 h-8 text-sm", colors.bgInput, colors.borderPrimary)}>
                              <Filter className="w-3 h-3 mr-1" />
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className={cn("z-[55]", colors.bgCard, colors.borderPrimary)}>
                              {epicFilterOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      {canEdit && (
                        <Button
                          size="sm"
                          onClick={() => setShowAddFeatureModal(true)}
                          className={cn("gap-1", colors.accentGradient)}
                          data-testid="persona-add-feature-btn"
                          disabled={refreshingFeatures}
                        >
                          <Plus className="w-4 h-4" />
                          Add Feature
                        </Button>
                      )}
                    </div>
                    
                    {/* Loading state */}
                    {refreshingFeatures ? (
                      <div className={cn("text-center py-12 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                        <Loader2 className={cn("w-8 h-8 mx-auto mb-3 animate-spin", colors.textAccent)} />
                        <p className={colors.textMuted}>Loading features...</p>
                      </div>
                    ) : filteredFeatures.length === 0 && pendingFeatureData.length === 0 ? (
                      <div className={cn("text-center py-12 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                        <Package className={cn("w-12 h-12 mx-auto mb-3 opacity-50", colors.textMuted)} />
                        <p className={colors.textMuted}>
                          {epicFilter === 'all' ? 'No features linked to this persona yet' : 'No features in this epic'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {/* Pending features from AI (not yet saved) */}
                        {pendingFeatureData.map(feature => {
                          const epic = allEpics.find(e => e.epic_id === feature.epic_id);
                          return (
                            <div
                              key={`pending-${feature.feature_id}`}
                              className={cn(
                                "p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md group",
                                "border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20"
                              )}
                              onClick={() => handleFeatureClick(feature)}
                              data-testid={`persona-pending-feature-${feature.feature_id}`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className={cn("font-medium truncate", colors.textPrimary)}>
                                      {feature.title}
                                    </h4>
                                    <Badge className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400">
                                      Pending
                                    </Badge>
                                    <Badge className={cn("text-xs", STATUS_COLORS[feature.status] || STATUS_COLORS['Backlog'])}>
                                      {feature.status || 'Backlog'}
                                    </Badge>
                                  </div>
                                  {epic && (
                                    <p className={cn("text-xs mb-2", colors.textMuted)}>
                                      Epic: {epic.title}
                                    </p>
                                  )}
                                  {feature.description && (
                                    <p className={cn("text-sm line-clamp-2", colors.textSecondary)}>
                                      {feature.description}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPendingFeatureLinks(prev => prev.filter(id => id !== feature.feature_id));
                                    }}
                                    className="p-1.5 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                                    title="Remove pending link"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Already linked features */}
                        {filteredFeatures.map(feature => {
                          const epic = allEpics.find(e => e.epic_id === feature.epic_id);
                          return (
                            <div
                              key={feature.feature_id}
                              className={cn(
                                "p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md group",
                                colors.bgCard, colors.borderPrimary
                              )}
                              onClick={() => handleFeatureClick(feature)}
                              data-testid={`persona-feature-${feature.feature_id}`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className={cn("font-medium truncate", colors.textPrimary)}>
                                      {feature.title}
                                    </h4>
                                    <Badge className={cn("text-xs", STATUS_COLORS[feature.status] || STATUS_COLORS['Backlog'])}>
                                      {feature.status || 'Backlog'}
                                    </Badge>
                                  </div>
                                  {epic && (
                                    <p className={cn("text-xs mb-2", colors.textMuted)}>
                                      Epic: {epic.title}
                                    </p>
                                  )}
                                  {feature.description && (
                                    <p className={cn("text-sm line-clamp-2", colors.textSecondary)}>
                                      {feature.description}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFeatureClick(feature);
                                    }}
                                    className={cn("p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700", colors.textMuted)}
                                    title="Open feature"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </button>
                                  {canEdit && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUnlinkFeature(feature);
                                      }}
                                      className="p-1.5 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                                      title="Unlink feature"
                                      data-testid={`unlink-feature-${feature.feature_id}`}
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                
                {/* ===== EPICS TAB ===== */}
                {activeTab === 'epics' && (
                  <div className="space-y-4">
                    {/* Header with add button */}
                    <div className="flex items-center justify-between">
                      <Label className={cn("flex items-center gap-2", colors.textSecondary)}>
                        <Layers className="w-4 h-4" />
                        Linked Epics
                      </Label>
                      {canEdit && (
                        <Button
                          size="sm"
                          onClick={() => setShowAddEpicModal(true)}
                          className={cn("gap-1", colors.accentGradient)}
                          data-testid="persona-add-epic-btn"
                        >
                          <Plus className="w-4 h-4" />
                          Add Epic
                        </Button>
                      )}
                    </div>
                    
                    {/* Empty state */}
                    {filteredLinkedEpics.length === 0 && pendingEpicData.length === 0 ? (
                      <div className={cn("text-center py-12 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                        <Layers className={cn("w-12 h-12 mx-auto mb-3 opacity-50", colors.textMuted)} />
                        <p className={colors.textMuted}>No epics linked to this persona yet</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {/* Pending epics from AI (not yet saved) */}
                        {pendingEpicData.map(epic => (
                          <div
                            key={`pending-epic-${epic.epic_id}`}
                            className={cn(
                              "p-4 rounded-lg border transition-all hover:shadow-md group",
                              "border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20"
                            )}
                            data-testid={`persona-pending-epic-${epic.epic_id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className={cn("font-medium truncate", colors.textPrimary)}>
                                    {epic.title}
                                  </h4>
                                  <Badge className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400">
                                    Pending
                                  </Badge>
                                  <Badge className={cn("text-xs", STATUS_COLORS[epic.status] || STATUS_COLORS['Backlog'])}>
                                    {epic.status || 'Backlog'}
                                  </Badge>
                                </div>
                                {epic.description && (
                                  <p className={cn("text-sm line-clamp-2", colors.textSecondary)}>
                                    {epic.description}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => setPendingEpicLinks(prev => prev.filter(id => id !== epic.epic_id))}
                                  className="p-1.5 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  title="Remove pending link"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {/* Already linked epics */}
                        {filteredLinkedEpics.map(epic => (
                          <div
                            key={epic.epic_id}
                            className={cn(
                              "p-4 rounded-lg border transition-all hover:shadow-md group",
                              colors.bgCard, colors.borderPrimary
                            )}
                            data-testid={`persona-epic-${epic.epic_id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className={cn("font-medium truncate", colors.textPrimary)}>
                                    {epic.title}
                                  </h4>
                                  <Badge className={cn("text-xs", STATUS_COLORS[epic.status] || STATUS_COLORS['Backlog'])}>
                                    {epic.status || 'Backlog'}
                                  </Badge>
                                </div>
                                {epic.description && (
                                  <p className={cn("text-sm line-clamp-2", colors.textSecondary)}>
                                    {epic.description}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                {canEdit && (
                                  <button
                                    onClick={() => handleUnlinkEpic(epic)}
                                    className="p-1.5 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                                    title="Unlink epic"
                                    data-testid={`unlink-epic-${epic.epic_id}`}
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* ===== ATTACHMENTS TAB ===== */}
                {activeTab === 'attachments' && persona?.persona_id && (
                  <AttachmentsPanel
                    entityType="persona"
                    entityId={persona?.persona_id}
                    productId={productContext?.product_id}
                    canEdit={canEdit}
                    data-testid="persona-attachments-panel"
                  />
                )}
                
                {/* ===== NOTES TAB ===== */}
                {activeTab === 'notes' && persona?.persona_id && (
                  <NotesPanel
                    entityType="persona"
                    entityId={persona.persona_id}
                    teamMembers={teamMembers}
                    colors={colors}
                  />
                )}
                
              </div>{/* End Tab Content */}
            </div>{/* End Left Panel */}
          
            {/* Right: AI Co-Pilot (40%) - Only shown for users with edit permission */}
            {showAICopilot && (
              <div className={cn("w-[40%] flex flex-col", colors.bgTertiary)} data-testid="persona-ai-copilot-panel">
                <UnifiedCoPilot
                  contextType="persona"
                  contextId={persona?.persona_id}
                  productId={productContext?.product_id}
                  contextData={formData}
                  relatedData={{
                    product: productContext,
                    linkedFeatures: linkedFeatures,
                    siblingPersonas: []  // Could pass other personas if needed
                  }}
                  onApplyUpdates={handleApplyUpdates}
                  canEditField={() => true}
                  onRefresh={refreshPersonaData}
                  variant="item"
                />
              </div>
            )}
          </div>
        </div>
        
        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-[70]">
            <div className="fixed inset-0 bg-black/70" onClick={() => setShowDeleteConfirm(false)} />
            <div className="fixed inset-0 flex items-center justify-center p-4">
              <div className={cn("w-full max-w-md rounded-xl shadow-2xl p-6", colors.bgCard)}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-full bg-red-500/10">
                    <Trash2 className="w-5 h-5 text-red-500" />
                  </div>
                  <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Delete Persona</h3>
                </div>
                
                <p className={cn("mb-2", colors.textSecondary)}>
                  Are you sure you want to delete this persona?
                </p>
                <p className={cn("text-sm mb-6 p-3 rounded-lg", colors.bgTertiary, colors.textPrimary)}>
                  &quot;{persona?.name}&quot;
                </p>
                <p className={cn("text-sm mb-6", colors.textMuted)}>
                  This action cannot be undone. Features linked to this persona will be unlinked.
                </p>
                
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleting}
                    className={cn("border", colors.borderPrimary)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="persona-confirm-delete-btn"
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Persona
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Unsaved Changes Warning Dialog */}
        {showUnsavedWarning && (
          <div className="fixed inset-0 z-[70]">
            <div className="fixed inset-0 bg-black/70" onClick={() => setShowUnsavedWarning(false)} />
            <div className="fixed inset-0 flex items-center justify-center p-4">
              <div className={cn("w-full max-w-md rounded-xl shadow-2xl p-6", colors.bgCard)}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-full bg-amber-500/10">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                  </div>
                  <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Unsaved Changes</h3>
                </div>
                
                <p className={cn("mb-6", colors.textSecondary)}>
                  You have unsaved changes. Are you sure you want to close without saving?
                </p>
                
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowUnsavedWarning(false)}
                    className={cn("border", colors.borderPrimary)}
                  >
                    Keep Editing
                  </Button>
                  <Button
                    onClick={() => {
                      setShowUnsavedWarning(false);
                      setHasChanges(false);
                      performClose();
                    }}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    Discard Changes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Clear History Confirmation Dialog */}
        {showClearHistoryConfirm && (
          <div className="fixed inset-0 z-[70]">
            <div className="fixed inset-0 bg-black/70" onClick={() => setShowClearHistoryConfirm(false)} />
            <div className="fixed inset-0 flex items-center justify-center p-4">
              <div className={cn("w-full max-w-md rounded-xl shadow-2xl p-6", colors.bgCard)}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-full bg-amber-500/10">
                    <Trash2 className="w-5 h-5 text-amber-500" />
                  </div>
                  <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>Clear Chat History</h3>
                </div>
                
                <p className={cn("mb-6", colors.textSecondary)}>
                  This will clear the chat history for all team members. This action cannot be undone.
                </p>
                
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowClearHistoryConfirm(false)}
                    className={cn("border", colors.borderPrimary)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleClearHistory}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear History
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Add Feature Modal */}
        <AddFeatureModal
          isOpen={showAddFeatureModal}
          onClose={() => setShowAddFeatureModal(false)}
          onSave={async () => {
            // Show loading state while refreshing
            setRefreshingFeatures(true);
            try {
              await onUpdate?.();
            } finally {
              // Small delay to ensure data is loaded before hiding loader
              setTimeout(() => setRefreshingFeatures(false), 500);
            }
          }}
          productId={productContext.product_id}
          personaId={persona?.persona_id}
          availableFeatures={availableFeaturesForLinking}
          allEpics={allEpics}
          onFeatureCreated={(epicId) => {
            // Set epic filter to the newly created feature's epic
            setEpicFilter(epicId);
            // Switch to Features tab to show the result
            setActiveTab('features');
            // Show loading indicator
            setRefreshingFeatures(true);
          }}
        />
        
        <AddEpicModal
          isOpen={showAddEpicModal}
          onClose={() => setShowAddEpicModal(false)}
          onSave={async () => {
            // Refresh data after linking/creating epic
            try {
              await onUpdate?.();
            } catch (err) {
              console.error('Failed to refresh after epic link:', err);
            }
          }}
          productId={productContext.product_id}
          personaId={persona?.persona_id}
          availableEpics={availableEpicsForLinking}
        />
      </div>
      </ModalZIndexProvider>
      
      {/* Feature Detail Modal - Opens on top of Persona Modal */}
      {selectedFeatureForModal && (
        <FeatureDetailModal
          isOpen={!!selectedFeatureForModal}
          onClose={() => setSelectedFeatureForModal(null)}
          feature={selectedFeatureForModal}
          productContext={productContext}
          onSave={() => onUpdate?.()} // Refresh parent data
        />
      )}
    </>
  );
};

export default PersonaDetailModal;
